import React, { useMemo } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  Box,
  Typography
} from '@mui/material';
import {
  Cake as BirthdateIcon,
  LocationOn as LocationIcon,
  Work as OccupationIcon,
  Update as UpdatedIcon
} from '@mui/icons-material';
import { format, differenceInYears } from 'date-fns';
import type { Person } from '../../../../types';

interface QuickFactsCardProps {
  person: Person;
}

interface FactItem {
  icon: React.ReactElement;
  label: string;
  value: string | null;
}

/**
 * QuickFactsCard - Quick reference facts about the person
 *
 * Displays key information:
 * - Birth date & age
 * - Current location
 * - Occupation
 * - Last updated
 */
export const QuickFactsCard: React.FC<QuickFactsCardProps> = ({ person }) => {
  const age = useMemo(() => {
    if (!person.birthDate) return null;

    if (person.isDeceased && person.deathDate) {
      return differenceInYears(
        new Date(person.deathDate),
        new Date(person.birthDate)
      );
    }

    return differenceInYears(new Date(), new Date(person.birthDate));
  }, [person.birthDate, person.deathDate, person.isDeceased]);

  const birthDateDisplay = useMemo(() => {
    if (!person.birthDate) return null;

    const formatted = format(new Date(person.birthDate), 'MMMM d, yyyy');
    return age !== null ? `${formatted} • Age ${age}` : formatted;
  }, [person.birthDate, age]);

  const locationDisplay = useMemo(() => {
    if (!person.currentLocation) return null;

    const parts = [
      person.currentLocation.city,
      person.currentLocation.state,
      person.currentLocation.country
    ].filter(Boolean);

    return parts.join(', ');
  }, [person.currentLocation]);

  const lastUpdatedDisplay = useMemo(() => {
    if (!person.updatedAt) return null;

    const now = new Date();
    const updated = new Date(person.updatedAt);
    const diffInSeconds = Math.floor((now.getTime() - updated.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return format(updated, 'MMM d, yyyy');
  }, [person.updatedAt]);

  const facts: FactItem[] = [
    {
      icon: <BirthdateIcon fontSize="small" />,
      label: 'Born',
      value: birthDateDisplay
    },
    {
      icon: <LocationIcon fontSize="small" />,
      label: 'Location',
      value: locationDisplay
    },
    {
      icon: <OccupationIcon fontSize="small" />,
      label: 'Occupation',
      value: person.occupation || null
    },
    {
      icon: <UpdatedIcon fontSize="small" />,
      label: 'Updated',
      value: lastUpdatedDisplay
    }
  ].filter(fact => fact.value !== null);

  // Don't render if no facts to display
  if (facts.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Quick Facts"
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
      />
      <CardContent sx={{ pt: 0, pb: '8px !important' }}>
        <List dense sx={{ py: 0 }}>
          {facts.map((fact) => (
            <ListItem
              key={fact.label}
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5
              }}
            >
              <Box
                sx={{
                  color: 'action.active',
                  display: 'flex',
                  alignItems: 'center',
                  pt: 0.25
                }}
              >
                {fact.icon}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 0.25
                  }}
                >
                  {fact.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    wordBreak: 'break-word'
                  }}
                >
                  {fact.value}
                </Typography>
              </Box>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
